import { useState, useEffect } from 'react';
import { AlertCircle, Calendar, TrendingUp, Percent, MoveRight } from 'lucide-react';
import {
  CRMProspect,
  CRMProspectStatus,
  STATUS_LABELS,
  SPT_CONFIG,
  getAllProspects,
  updateProspect,
} from '../../../utils/crmScoringService';

const PIPELINE_STATUSES: CRMProspectStatus[] = [
  'insomnie_detectee',
  'probleme_formule',
  'demo_realisee',
  'projection_envoyee',
  'decision_en_cours',
  'pilote_actif',
];

export function CRMPipelineKanban({ onProspectClick }: { onProspectClick: (prospect: CRMProspect) => void }) {
  const [prospects, setProspects] = useState<CRMProspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProspect, setSelectedProspect] = useState<string | null>(null);

  useEffect(() => {
    loadProspects();
  }, []);

  async function loadProspects() {
    try {
      const data = await getAllProspects();
      setProspects(data.filter(p => p.status !== 'perdu' && p.status !== 'active'));
    } catch (error) {
      console.error('Error loading prospects:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(prospectId: string, newStatus: CRMProspectStatus) {
    try {
      await updateProspect(prospectId, { status: newStatus });

      setProspects(prev =>
        prev.map(p =>
          p.id === prospectId
            ? { ...p, status: newStatus }
            : p
        )
      );
      setSelectedProspect(null);
    } catch (error) {
      console.error('Error updating prospect status:', error);
    }
  }

  function getProspectsByStatus(status: CRMProspectStatus): CRMProspect[] {
    return prospects.filter(p => p.status === status);
  }

  function getSPTBadgeClasses(sptScore?: number) {
    const config = SPT_CONFIG[sptScore as keyof typeof SPT_CONFIG];
    if (!config) return 'bg-gray-100 text-gray-800';

    const colorMap = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      red: 'bg-red-100 text-red-800 border-red-200',
    };

    return colorMap[config.color as keyof typeof colorMap] || 'bg-gray-100 text-gray-800';
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {PIPELINE_STATUSES.map(status => {
          const statusProspects = getProspectsByStatus(status);

          return (
            <div key={status} className="flex flex-col bg-gray-50 rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200 bg-white rounded-t-lg">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {STATUS_LABELS[status]}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {statusProspects.length} prospect{statusProspects.length !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="flex-1 p-2 min-h-[200px] space-y-2">
                {statusProspects.map(prospect => (
                  <div
                    key={prospect.id}
                    className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-shadow group relative"
                  >
                    <div
                      className="absolute inset-0 rounded-lg"
                      onClick={() => onProspectClick(prospect)}
                    />

                    {selectedProspect === prospect.id && (
                      <div className="absolute top-2 right-2 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-10">
                        <div className="text-xs font-medium text-gray-700 mb-1">Déplacer vers:</div>
                        <div className="space-y-1">
                          {PIPELINE_STATUSES.filter(s => s !== status).map(s => (
                            <button
                              key={s}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(prospect.id!, s);
                              }}
                              className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded flex items-center gap-1"
                            >
                              <MoveRight size={10} />
                              {STATUS_LABELS[s]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-sm text-gray-900 flex-1 pr-2">
                        {prospect.organization_name}
                      </h4>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-1 text-xs font-bold rounded border ${getSPTBadgeClasses(
                            prospect.spt_score
                          )}`}
                        >
                          SPT {prospect.spt_score}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProspect(selectedProspect === prospect.id ? null : prospect.id!);
                          }}
                          className="relative z-10 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded border border-blue-200 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Déplacer
                        </button>
                      </div>
                    </div>

                    {prospect.sector && (
                      <p className="text-xs text-gray-500 mb-2">{prospect.sector}</p>
                    )}

                    {prospect.targeted_offer && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 mb-2">
                        <TrendingUp size={12} />
                        <span>{prospect.targeted_offer}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <AlertCircle size={12} />
                        <span>{prospect.survival_months}m</span>
                      </div>

                      {prospect.next_major_due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar size={12} />
                          <span>
                            {new Date(prospect.next_major_due_date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {prospect.estimated_mrr > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                        <span className="font-semibold text-green-700">
                          {prospect.estimated_mrr.toLocaleString('fr-FR')} €/m
                        </span>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Percent size={10} />
                          <span>{Math.round((prospect.dynamic_probability || 0) * 100)}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
